import { useState } from 'react';
import { toBlob, toPng } from 'html-to-image';
import { Options } from 'html-to-image/lib/types';

import { isServerSide } from '@blockworks/platform';
import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { ColorModeClass } from '@blockworks/ui/models';

import { useVisualizationContext } from '../../context';
import { LEGEND_HTML_ID, LEGEND_INNER_HTML_ID } from '../../models';
import {
    BRUSH_BOX_CLASSNAME,
    BRUSH_BOX_HTML_ID,
    BRUSH_HANDLE_CLASSNAME,
    GRID_LINE_CLASSNAME,
    TICK_LABEL_CLASSNAME,
} from '../charts/timeseries/timeseries.model';
import {
    PARAMETER_CONTROLS_CONTAINER_HEIGHT,
    PARAMETER_CONTROLS_CONTAINER_HTML_ID,
} from '../parameter-controls/parameter-controls.model';
import { TABLE_FOOTER_HEIGHT, TABLE_FOOTER_HTML_ID } from '../table/models';
import {
    VISUALIZATION_HEADER_HEIGHT,
    VISUALIZATION_HEADER_HTML_ID,
} from '../visualization-header/visualization-header.model';
import {
    FEATURE_CONTROL_BUTTONS_BAR_ID,
    VISUALIZATION_INNER_WRAPPER_HTML_ID,
    VISUALIZATION_WORDMARK_HTML_ID,
} from '../visualization-renderer/visualization-renderer.model';

const filterNodes =
    (removeBranding = false) =>
    (node: HTMLElement) => {
        if (removeBranding) {
            return (
                node.id !== VISUALIZATION_HEADER_HTML_ID &&
                node.id !== TABLE_FOOTER_HTML_ID &&
                node.id !== VISUALIZATION_WORDMARK_HTML_ID &&
                node.id !== PARAMETER_CONTROLS_CONTAINER_HTML_ID &&
                node.id !== BRUSH_BOX_HTML_ID
            );
        }
        return node.id !== TABLE_FOOTER_HTML_ID && node.id !== FEATURE_CONTROL_BUTTONS_BAR_ID;
    };

const getRemovableElementsHeight = (visualizationEl?: HTMLDivElement | null) => {
    if (!visualizationEl && isServerSide()) return VISUALIZATION_HEADER_HEIGHT / 2;
    const el = visualizationEl || document;
    const visualizationHeader = el.querySelector(`#${VISUALIZATION_HEADER_HTML_ID}`);
    const halfHeaderHeight = (visualizationHeader?.getBoundingClientRect?.().height ?? VISUALIZATION_HEADER_HEIGHT) / 2;
    const parameterControls = el.querySelector(`#${PARAMETER_CONTROLS_CONTAINER_HTML_ID}`);
    if (!parameterControls) return halfHeaderHeight;
    return PARAMETER_CONTROLS_CONTAINER_HEIGHT + halfHeaderHeight;
};

const getImgOptions = (type: StudioVisualizationType, visualizationRef: HTMLDivElement, removeBranding = false) => {
    const isDarkMode = document.documentElement.classList.contains(ColorModeClass.Dark);
    const options: Options = {
        cacheBust: true,
        filter: filterNodes(removeBranding),
        backgroundColor: removeBranding ? 'transparent' : isDarkMode ? '#1A1B1E' : '#FFFFFF',
    };

    if (removeBranding) {
        options.width = visualizationRef.offsetWidth;
        /** getRemovableElementsHeight() value is added to the height in the unbranded screenshot component to account for the removal of the parameter controls */
        options.height = visualizationRef.offsetHeight - getRemovableElementsHeight(visualizationRef);
    }
    // if visualization is a table, we need to remove the height accounted for by the table footer
    if (type === StudioVisualizationType.Table) {
        let height = visualizationRef.scrollHeight;
        if (typeof options.height === 'number') {
            height = options.height;
        }
        options.height = height - TABLE_FOOTER_HEIGHT - 12; // 12 accounts for gap between table and footer
    }

    return options;
};

const appendSourceAttribution = (visualizationEl: HTMLDivElement | null) => {
    if (!visualizationEl) return;
    const sourceMark = document.createElement('div');
    sourceMark.textContent = 'Source: Blockworks Research';

    sourceMark.className = 'dark:bg-surface-muted bg-surface-raised';
    Object.assign(sourceMark.style, {
        position: 'absolute',
        top: `10px`,
        right: '12px',
        zIndex: '10',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 6px',
        border: `1px solid var(--color-surface-divider)`,
        borderRadius: '2px',
        color: 'var(--color-content)',
        fontSize: '9px',
        fontWeight: '500',
    });

    visualizationEl.appendChild(sourceMark);
};

const shiftContentUp = (visualizationEl: HTMLDivElement | null) => {
    if (!visualizationEl) return;
    const content = visualizationEl.querySelector(`#${VISUALIZATION_INNER_WRAPPER_HTML_ID}`) as HTMLDivElement;

    if (!content) return;
    const visualizationHeader = content.querySelector(`#${VISUALIZATION_HEADER_HTML_ID}`);
    const headerOffset = (visualizationHeader?.getBoundingClientRect().height ?? VISUALIZATION_HEADER_HEIGHT) / 1.5;
    Object.assign(content.style, {
        transform: `translateY(-${getRemovableElementsHeight(visualizationEl) + headerOffset}px)`,
    });
};

const hideScrollbars = (visualizationEl: HTMLDivElement | null) => {
    if (visualizationEl) {
        visualizationEl.style.overflow = 'hidden';
        const legendEl = visualizationEl.querySelector(`#${LEGEND_HTML_ID}`) as HTMLDivElement;
        if (legendEl) {
            legendEl.style.overflow = 'hidden';
        }
        const legendInnerEl = visualizationEl.querySelector(`#${LEGEND_INNER_HTML_ID}`) as HTMLDivElement;
        if (legendInnerEl) {
            legendInnerEl.style.overflow = 'hidden';
        }
    }
};

/**
 * A helper to compute styles for SVG elements that are not directly accessible.
 *
 * We need to compute styles for these SVG elements because css vars don't resolve
 * (in screenshots) like they do for HTML elements.
 */
const setComputedBrushStyles = (visualizationEl: HTMLDivElement | null) => {
    if (visualizationEl) {
        const brushHandles = visualizationEl.querySelectorAll(`.${BRUSH_HANDLE_CLASSNAME}`);
        const brushBox = visualizationEl.querySelector(`.${BRUSH_BOX_CLASSNAME}`);
        const gridLines = visualizationEl.querySelectorAll(`.${GRID_LINE_CLASSNAME}`);
        const rootStyles = window.getComputedStyle(document.documentElement);
        const stroke = rootStyles.getPropertyValue('--color-surface-interact');
        const fill = rootStyles.getPropertyValue('--color-current-bg');

        brushHandles.forEach(handle => {
            const fillAttr = handle.getAttribute('fill');
            if (fillAttr?.startsWith('var(')) {
                handle.setAttribute('fill', fill);
            }
            const strokeAttr = handle.getAttribute('stroke');
            if (strokeAttr?.startsWith('var(')) {
                handle.setAttribute('stroke', stroke);
            }
        });

        if (brushBox) {
            const strokeAttr = brushBox.getAttribute('stroke');
            if (strokeAttr?.startsWith('var(')) {
                brushBox.setAttribute('stroke', stroke);
            }
        }

        gridLines.forEach(gridLine => {
            gridLine.setAttribute('stroke', stroke);
            gridLine.setAttribute('stroke-width', '0.5');
            gridLine.setAttribute('style', '');
        });
    }
};

const setComputedTickLabelStyles = (visualizationEl: HTMLDivElement | null) => {
    if (visualizationEl) {
        const tickLabels = visualizationEl.querySelectorAll(`.${TICK_LABEL_CLASSNAME}`);
        const rootStyles = window.getComputedStyle(document.documentElement);
        const fill = rootStyles.getPropertyValue('--color-content-muted');

        tickLabels.forEach(tick => {
            const shouldFill = tick.classList.contains('fill-content-muted');
            if (shouldFill) {
                tick.setAttribute('fill', fill);
            }
        });
    }
};

const setTransparentLegendBorder = (visualizationEl: HTMLDivElement | null) => {
    if (visualizationEl) {
        const legendEl = visualizationEl.querySelector(`#${LEGEND_HTML_ID}`) as HTMLDivElement;
        if (legendEl) {
            legendEl.style.borderColor = 'transparent';
        }
    }
};

/**
 * Hook provides helpers to clone a visualization and adjust the DOM to produce a
 * visually pleasing image.
 *
 * @todo clean up the uglyness - this is too messy
 */
const useCloneVisualization = (removeBranding?: boolean) => {
    const { visualizationRef } = useVisualizationContext();
    const visualizationContainer = visualizationRef.current?.parentElement;
    const [originalOverflow, setOriginalOverflow] = useState<string>();

    const create = () => {
        if (!visualizationRef.current || !visualizationContainer) return;

        const clonedVisualization = visualizationContainer.cloneNode(true) as HTMLDivElement;

        Object.assign(clonedVisualization.style, {
            position: 'asbolute',
            zIndex: '-1',
        });

        setOriginalOverflow(visualizationContainer.style.overflow);
        if (visualizationContainer.style) {
            Object.assign(visualizationContainer.style, {
                overflow: 'hidden',
            });
        }

        hideScrollbars(clonedVisualization);
        setComputedTickLabelStyles(clonedVisualization);
        if (!removeBranding) {
            setComputedBrushStyles(clonedVisualization);
            appendSourceAttribution(clonedVisualization);
        } else {
            setTransparentLegendBorder(clonedVisualization);
            shiftContentUp(clonedVisualization);
        }

        visualizationContainer.appendChild(clonedVisualization);

        return clonedVisualization;
    };

    const cleanup = (element?: HTMLDivElement | null) => {
        if (!element || !visualizationContainer) return;
        visualizationContainer.removeChild(element);
        visualizationContainer.style.overflow = originalOverflow ?? '';
        setOriginalOverflow(undefined);
    };

    return {
        create,
        cleanup,
    };
};

const getUploadableVisualizationImage = async (
    visualizationElement: HTMLDivElement,
    visualizationType: StudioVisualizationType,
    callback: (blob: Blob | null) => Promise<void>,
) => {
    const image = await toBlob(visualizationElement, getImgOptions(visualizationType, visualizationElement, false));
    await callback(image);
};

const downloadVisualizationImage = async (
    visualizationElement: HTMLDivElement,
    visualizationType: StudioVisualizationType,
    title?: string,
    removeBranding?: boolean,
) => {
    const dataUrl = await toPng(
        visualizationElement,
        getImgOptions(visualizationType, visualizationElement, removeBranding),
    );

    if (!dataUrl) {
        throw new Error('Failed to convert HTML element to PNG');
    }
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${title ?? 'visualization'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const copyVisualizationImageToClipboard = async (
    visualizationElement: HTMLDivElement,
    visualizationType: StudioVisualizationType,
    callback?: () => void,
    removeBranding = false,
) => {
    if (navigator.clipboard && navigator.clipboard.write) {
        const blob = await toBlob(visualizationElement, {
            ...getImgOptions(visualizationType, visualizationElement, removeBranding),
            type: 'image/png',
        });

        if (!blob) {
            throw new Error('Failed to convert HTML element to Blob');
        }

        const clipboardItem = new ClipboardItem({ 'image/png': blob });

        await navigator.clipboard.write([clipboardItem]);
        callback?.();
    } else {
        console.error('Clipboard API not supported in this browser.');
    }
};

export {
    copyVisualizationImageToClipboard,
    downloadVisualizationImage,
    getRemovableElementsHeight,
    getUploadableVisualizationImage,
    useCloneVisualization,
};
